import { GamePhase, GameState, TileType, CatState, Direction } from '../types';
import { Player } from '../entities/Player';
import { Cat, CatConfig } from '../entities/Cat';
import { CheeseItem, initCheese, getCheeseAt } from '../entities/Cheese';
import { Treat, createTreat } from '../entities/PowerUp';
import { Renderer } from './Renderer';
import { InputManager } from './InputManager';
import { SoundManager } from './SoundManager';
import { checkPlayerCatCollision } from '../systems/CollisionSystem';
import { getTargetTile } from '../systems/CatAI';
import {
  createInitialState,
  addCheeseScore,
  addPowerCheeseScore,
  addCatPounceScore,
  addTreatScore,
  loseLife,
  nextLevel,
} from '../systems/ScoreManager';
import { getConfigForLevel, shouldSpawnTreat } from '../systems/LevelManager';
import { getMazeForLevel, countCheese, CAT_HOUSE_POSITIONS } from '../maps/levels';

const CAT_CONFIGS: CatConfig[] = [
  { name: 'Whiskers', color: '#e8713a', startTile: CAT_HOUSE_POSITIONS[0], scatterTarget: { col: 25, row: 1 } },
  { name: 'Shadow', color: '#333333', startTile: CAT_HOUSE_POSITIONS[1], scatterTarget: { col: 2, row: 1 } },
  { name: 'Mittens', color: '#8899aa', startTile: CAT_HOUSE_POSITIONS[2], scatterTarget: { col: 25, row: 28 } },
  { name: 'Chonk', color: '#e8e0d0', startTile: CAT_HOUSE_POSITIONS[3], scatterTarget: { col: 2, row: 28 } },
];

export type StateListener = (state: GameState) => void;

export class GameLoop {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderer: Renderer;
  private input: InputManager;
  private sound: SoundManager;
  private player!: Player;
  private cats: Cat[] = [];
  private cheese: CheeseItem[] = [];
  private treat: Treat | null = null;
  private maze: number[][] = [];
  private state!: GameState;
  private rafId: number = 0;
  private lastTime: number = 0;
  private elapsed: number = 0;
  private phaseTimer: number = 0;
  private huntPatrolTimer: number = 0;
  private inHuntMode: boolean = true;
  private stateListeners: StateListener[] = [];
  private resizeObserver: ResizeObserver | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.input = new InputManager();
    this.sound = new SoundManager();

    const config = getConfigForLevel(1);
    this.renderer = new Renderer(this.ctx, config.tileSize);

    this.initLevel(1);
  }

  private initLevel(level: number): void {
    const config = getConfigForLevel(level);
    const ts = config.tileSize;

    this.maze = getMazeForLevel(level);
    const totalCheese = countCheese(this.maze);

    if (!this.state) {
      this.state = createInitialState(totalCheese);
    }

    this.player = new Player(ts, config.playerSpeed);
    this.cats = CAT_CONFIGS.map((cc, i) =>
      new Cat(cc, ts, config.catSpeed, config.scaredCatSpeed, i * 2)
    );
    this.cheese = initCheese(this.maze);
    this.treat = null;
    this.phaseTimer = 0;
    this.huntPatrolTimer = 0;
    this.inHuntMode = true;
    this.input.reset();
  }

  start(): void {
    this.sound.init();
    this.input.attach(this.canvas);

    const parent = this.canvas.parentElement;
    if (parent) {
      this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
      this.resizeObserver.observe(parent);
    }

    this.resizeCanvas();
    this.lastTime = performance.now();
    this.tick(this.lastTime);
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
    this.input.detach(this.canvas);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  onStateChange(listener: StateListener): () => void {
    this.stateListeners.push(listener);
    return () => {
      this.stateListeners = this.stateListeners.filter(l => l !== listener);
    };
  }

  getState(): GameState {
    return this.state;
  }

  getInputManager(): InputManager {
    return this.input;
  }

  getSoundManager(): SoundManager {
    return this.sound;
  }

  private resizeCanvas(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    if (w === 0 || h === 0) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.renderer.resize(w, h);
  }

  private tick = (now: number): void => {
    const rawDt = (now - this.lastTime) / 1000;
    const dt = Math.min(rawDt, 0.05);
    this.lastTime = now;
    this.elapsed += dt;

    this.update(dt);
    this.renderer.render(
      this.maze,
      this.player,
      this.cats,
      this.cheese,
      this.treat,
      this.state,
      this.elapsed,
    );

    this.rafId = requestAnimationFrame(this.tick);
  };

  private update(dt: number): void {
    switch (this.state.phase) {
      case GamePhase.Ready:
        this.phaseTimer += dt;
        if (this.phaseTimer >= 2) {
          this.state = { ...this.state, phase: GamePhase.Playing };
          this.phaseTimer = 0;
          this.emitState();
        }
        break;

      case GamePhase.Playing:
        this.updatePlaying(dt);
        break;

      case GamePhase.Dying:
        this.phaseTimer += dt;
        if (this.phaseTimer >= 1.5) {
          this.phaseTimer = 0;
          this.player.reset();
          this.cats.forEach(c => c.reset());
          this.state = { ...this.state, phase: GamePhase.Ready };
          this.input.reset();
          this.emitState();
        }
        break;

      case GamePhase.LevelComplete:
        this.phaseTimer += dt;
        if (this.phaseTimer >= 2) {
          this.phaseTimer = 0;
          const config = getConfigForLevel(this.state.level + 1);
          const newMaze = getMazeForLevel(this.state.level + 1);
          const totalCheese = countCheese(newMaze);
          this.state = nextLevel(this.state, totalCheese);
          this.initLevel(this.state.level);
          this.state = { ...this.state, phase: GamePhase.Ready };
          this.emitState();
        }
        break;

      case GamePhase.CatCaught:
        this.phaseTimer += dt;
        if (this.phaseTimer >= 0.5) {
          this.phaseTimer = 0;
          this.state = { ...this.state, phase: GamePhase.Playing };
        }
        break;

      case GamePhase.GameOver:
        break;

      case GamePhase.Paused:
        break;
    }
  }

  private updatePlaying(dt: number): void {
    const config = getConfigForLevel(this.state.level);

    // Hunt / patrol cycling
    this.huntPatrolTimer += dt;
    const cycleDuration = this.inHuntMode ? config.huntDuration : config.patrolDuration;
    if (this.huntPatrolTimer >= cycleDuration) {
      this.huntPatrolTimer = 0;
      this.inHuntMode = !this.inHuntMode;
    }

    // Player
    const queuedDir = this.input.getQueuedDirection();
    this.player.update(dt, this.maze, queuedDir);
    if (queuedDir !== Direction.None) {
      this.input.clearQueue();
    }

    // Cheese collection
    const cheeseHere = getCheeseAt(this.cheese, this.player.tilePos.col, this.player.tilePos.row);
    if (cheeseHere) {
      cheeseHere.eaten = true;
      this.maze[cheeseHere.row][cheeseHere.col] = TileType.Empty;

      if (cheeseHere.type === TileType.PowerCheese) {
        this.state = addPowerCheeseScore(this.state);
        this.cats.forEach(c => c.scare(config.scaredDuration));
        this.sound.powerUp();
      } else {
        this.state = addCheeseScore(this.state);
        this.sound.chomp();
      }

      if (shouldSpawnTreat(this.state.cheeseRemaining, this.state.totalCheese) && !this.treat) {
        this.treat = createTreat(this.state.level);
      }

      if (this.state.cheeseRemaining <= 0) {
        this.state = { ...this.state, phase: GamePhase.LevelComplete };
        this.phaseTimer = 0;
        this.sound.levelComplete();
        this.emitState();
        return;
      }
      this.emitState();
    }

    // Treat
    if (this.treat && this.treat.active) {
      this.treat.timer -= dt;
      if (this.treat.timer <= 0) {
        this.treat.active = false;
      } else if (
        this.player.tilePos.col === this.treat.col &&
        this.player.tilePos.row === this.treat.row
      ) {
        this.state = addTreatScore(this.state, this.treat.points);
        this.treat.active = false;
        this.sound.treatCollected();
        this.emitState();
      }
    }

    // Cats
    const baseCatState = this.inHuntMode ? CatState.Hunt : CatState.Patrol;
    for (let i = 0; i < this.cats.length; i++) {
      const cat = this.cats[i];
      const effectiveState = (cat.state === CatState.Scared || cat.state === CatState.Fleeing ||
                              cat.state === CatState.InHouse || cat.state === CatState.Exiting)
        ? cat.state
        : baseCatState;

      const target = getTargetTile(
        cat,
        i,
        this.player.tilePos,
        this.player.direction,
        this.cats[0].tilePos,
        effectiveState,
      );
      cat.update(dt, this.maze, target);
    }

    // Collisions
    for (const cat of this.cats) {
      const result = checkPlayerCatCollision(this.player, cat, config.tileSize);
      if (result === 'pounce') {
        cat.pounce();
        this.state = addCatPounceScore(this.state);
        this.state = { ...this.state, phase: GamePhase.CatCaught };
        this.phaseTimer = 0;
        this.sound.catPounced();
        this.emitState();
      } else if (result === 'caught') {
        this.state = loseLife(this.state);
        this.phaseTimer = 0;
        this.sound.caught();
        this.emitState();
        return;
      }
    }
  }

  private emitState(): void {
    for (const listener of this.stateListeners) {
      listener(this.state);
    }
  }
}
